import {
  OfferingCreateDTOAllOf,
  OfferingDTOAllOf,
  OfferingUpdateDTO,
  OfferingWithBobcatOffersDTOAllOf,
} from '@/api/reseller';
import lltLogo from '@/assets/branding/llt-logo.png';
import lltLogoDark from '@/assets/branding/llt-logo-dark.png';
import ltnLogo from '@/assets/branding/ltn-logo.svg';
import ltnLogoDark from '@/assets/branding/ltn-logo-dark.png';
import ltvLogo from '@/assets/branding/ltv-logo.svg';
import ltvLogoDark from '@/assets/branding/ltv-logo-dark.png';
import { ReactNode } from 'react';

export type CompleteOffering = Omit<OfferingUpdateDTO, 'description'> &
  OfferingCreateDTOAllOf &
  OfferingDTOAllOf &
  OfferingWithBobcatOffersDTOAllOf &
  OfferingSupplemental;

export interface BirthYearRange {
  min?: number;
  max?: number;
}

export interface AgeRange {
  min?: number;
  max?: number;
}

export interface OfferingSupplemental {
  url: string;
  logo: string;
  logoDark: string;
  name: string;
  welcome: ReactNode;
  description: ReactNode;
  bgColor: string;
  ageRange?: AgeRange;
  birthYearRange?: BirthYearRange;
  customAgeError?: string;
  recipientNinLabel?: string;
  tenantId: string;
  deliveryMethods: DeliveryMethodInfo[];
}

export type DeliveryMethod = 'app' | 'card' | 'other';

export interface DeliveryMethodInfo {
  type: DeliveryMethod;
  title: string;
  description: string;
  grantedDescription?: ReactNode;
}

const LLT_WELCOME = (
  <>
    <p>
      Under sommarlovet <strong>13 juni till 23 augusti</strong> kan du åka
      gratis buss med LLT. Detta gäller för alla barn och ungdomar upp till 19
      år, födda 2007-2019. Erbjudandet gäller även bussresor inom länet med
      Länstrafiken Norrbotten, Boden lokaltrafik Citybuss, Gällivare
      Lokaltrafik, Haparanda Ringlinjen, Kalix Lokaltrafik, Kiruna Lokaltrafik
      och Piteå Stadsbuss.
    </p>
    <b>
      LLT:s app och LLT:s resekort gäller i hela länet under den här perioden.
    </b>
  </>
);

const LLT_DESCRIPTION = (
  <>
    <p>
      Detta gäller för alla barn och ungdomar upp till 19 år, födda 2007-2019.
    </p>
    <p>Ansökan görs av vårdnadshavare eller ungdom över 18 år.</p>
  </>
);

const LLT_METHOD_APP =
  'Viktigt! Använd inte vårdnadshavarens appkonto. Barnet/ungdomen måste ha registrerat ett eget konto och e-postadress i appen.';

const TICKET_AVAILABLE = (
  <p>
    Sommarlovsresor finns nu tillgänglig för biljettmottagaren och gäller under
    perioden 13/6 - 23/8.
  </p>
);

const LTN_WELCOME = (
  <>
    <p>
      Gratis kollektivtrafik i Norrbotten under sommarlovet för barn och
      ungdomar 7-19 år.
    </p>
    <p>
      Erbjudandet gäller under perioden 13/6 - 23/8 2026 för bussresor inom
      länet med Länstrafiken Norrbotten, Boden lokaltrafik Citybuss, Gällivare
      Lokaltrafik, Haparanda Ringlinjen, Kalix Lokaltrafik, Kiruna Lokaltrafik,
      Luleå Lokaltrafik och Piteå Stadsbuss.
    </p>
    <p>Ansökan görs av vårdnadshavare eller ungdom över 18 år.</p>
    <p>
      <strong>Viktigt!</strong> Barnet/ungdomen måste registrera ett eget konto
      i Länstrafikens app eller ha ett busskort/skolkort{' '}
      <span className="underline">innan</span> du loggar in och gör en ansökan.
    </p>
    <p>
      Mer information finns på webben:{' '}
      <a
        href="https://lanstrafikennorrbotten.se/sommarlovsresor"
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold hover:underline"
      >
        lanstrafikennorrbotten.se/sommarlovsresor
      </a>
    </p>
  </>
);

const LTN_DESCRIPTION = (
  <>
    <p>
      Gratis kollektivtrafik i Norrbotten under sommarlovet för barn och
      ungdomar 7-19 år.
    </p>
    <p>
      Ansökan görs av vårdnadshavare eller ungdom över 18 år. Max 5
      barn/ungdomar per vårdnadshavare.
    </p>
  </>
);

const LTV_WELCOME = (
  <>
    <p>
      Gratis kollektivtrafik inom Umeå Kommun under sommarlovet för barn 7-19
      år.
    </p>
    <p>Erbjudandet gäller under Sommarlovet 2026 (11 juni - 16 augusti).</p>
    <p>
      Förälder, vårdnadshavare eller barn med BankID kan ladda ner biljetten.
    </p>
    <p>
      <strong>Viktigt!</strong> Barnet måste registrera ett eget konto i någon
      av våra appar "Tabussen.nu" eller "Ultra - Umeås lokaltrafik". Alternativt
      ha ett busskort/skolkort innan du loggar in och laddar ner biljetten.
    </p>
  </>
);

const LTV_DESCRIPTION = (
  <>
    <p>
      Gratis kollektivtrafik inom Umeå Kommun under sommarlovet för barn 7-19
      år.
    </p>
    <p>
      Förälder, vårdnadshavare eller barn med BankID kan ladda ner biljetten.
    </p>
  </>
);

const LTV_TICKET_AVAILABLE = (
  <p>
    Erbjudandet finns nu tillgängligt för biljettmottagaren och gäller under
    perioden 11/6 - 16/8.
  </p>
);

const LTN_METHOD_APP =
  'Viktigt! Använd inte vårdnadshavarens appkonto. Barnet/ungdomen måste ha registrerat ett eget konto och e-postadress i Länstrafikens app.';

const LTN_METHOD_CARD =
  'Om du inte har något busskort, beställ ett kort via Mina Sidor på webben eller hämta ett kort på en bemannad busstation.';

const LTV_METHOD_APP =
  'Viktigt! Använd inte vårdnadshavarens appkonto. Barnet/ungdomen måste ha registrerat ett eget konto och e-postadress i appen.';

const LTV_METHOD_CARD =
  'Om du inte har något busskort, beställ ett kort via Mina Sidor på webben eller hämta ett kort på Reseinfo.';

export const OFFERINGS = Object.freeze<Record<string, OfferingSupplemental>>({
  'lulea-sommarkort-2026': {
    url: 'lulea-sommarkort-2026',
    logo: lltLogo,
    logoDark: lltLogoDark,
    name: 'Luleå sommarkort 2026',
    welcome: LLT_WELCOME,
    description: LLT_DESCRIPTION,
    bgColor: '#E30613',
    birthYearRange: { min: 2007, max: 2019 },
    tenantId: 'llt',
    deliveryMethods: [
      {
        type: 'app',
        title: 'I appen "Luleå Lokaltrafik"',
        description: LLT_METHOD_APP,
      },
      {
        type: 'card',
        title: 'Befintligt resekort',
        description: 'Ange 16-siffrigt kortnummer (finns på kortets baksida)',
        grantedDescription: (
          <>
            {TICKET_AVAILABLE}
            <p>Det nyttjas genom det valda kortet.</p>
          </>
        ),
      },
      {
        type: 'other',
        title: 'Hämta ut fysiskt resekort på LLT info',
        description: 'Gå till LLT Info för att hämta ut nytt fysiskt resekort',
        grantedDescription: (
          <p>
            Gå till LLT Info för att hämta ut ett fysiskt resekort med
            sommarlovsresor mot uppvisande av bekräftelse
          </p>
        ),
      },
    ],
  },
  'ltn-sommarlovsresor-2026': {
    url: 'ltn-sommarlovsresor-2026',
    logo: ltnLogo,
    logoDark: ltnLogoDark,
    name: 'Sommarlovsresor Norrbotten',
    welcome: LTN_WELCOME,
    description: LTN_DESCRIPTION,
    bgColor: '#18436C',
    birthYearRange: { min: 2007, max: 2019 },
    tenantId: 'ltn',
    deliveryMethods: [
      {
        type: 'app',
        title: 'I appen "Länstrafiken Norrbotten"',
        description: LTN_METHOD_APP,
        grantedDescription: (
          <>
            {TICKET_AVAILABLE}
            <p>
              Det nyttjas i appen på en enhet som har den angivna e-posten
              kopplad.
            </p>
          </>
        ),
      },
      {
        type: 'card',
        title: 'Befintligt busskort/skolkort',
        description: LTN_METHOD_CARD,
        grantedDescription: (
          <>
            {TICKET_AVAILABLE}
            <p>Det nyttjas genom det valda kortet.</p>
          </>
        ),
      },
    ],
  },
  'ltv-sommarlovsresor-2026': {
    url: 'ltv-sommarlovsresor-2026',
    logo: ltvLogo,
    logoDark: ltvLogoDark,
    name: 'Sommarlovsbiljett Umeå Kommun',
    welcome: LTV_WELCOME,
    description: LTV_DESCRIPTION,
    recipientNinLabel: 'Barnets personnummer (12 siffror)',
    bgColor: '#00406E',
    birthYearRange: { max: 2019 },
    ageRange: { max: 19 },
    customAgeError: 'Mottagaren måste vara 7-19 år.',
    tenantId: 'ltv',
    deliveryMethods: [
      {
        type: 'app',
        title: 'I appen "Tabussen.nu" eller "Ultra - Umeås lokaltrafik"',
        description: LTV_METHOD_APP,
        grantedDescription: (
          <>
            {LTV_TICKET_AVAILABLE}
            <p>
              Det nyttjas i appen på en enhet som har den angivna e-posten
              kopplad.
            </p>
          </>
        ),
      },
      {
        type: 'card',
        title: 'Befintligt busskort/skolkort',
        description: LTV_METHOD_CARD,
        grantedDescription: (
          <>
            {LTV_TICKET_AVAILABLE}
            <p>Det nyttjas genom det valda kortet.</p>
          </>
        ),
      },
    ],
  },
});
